import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getSnapshotDetails } from "../../../app/api/trendSnapshot";
import { RootState } from "../../../app/store";
import { ReactComponent as Minus } from "../../../assets/icons/minus.svg";
import { ReactComponent as Plus } from "../../../assets/icons/plus.svg";
import { BarData, DataNode } from "../../../interface/Trends";
import Dropdown from "../../Common/Dropdown";
import OverallAnalysisGraph from "../TrendsSidebar/OverallAnalysisGraph";
import ColorAnalysisSnapShot from "./ColorAnalysisSnapshot";
import { getFactoredTrendCurveResponse } from "../../../utils/commonUtils";
import { LineGraphData } from "../../../interface/DesignerInterface";
import TrendCurvesGraph from "../TrendsSidebar/TrendCurvesGraph";

const GraphsSidebarForSnapshot = () => {
  const [colorAnalysisOpen, setColorAnalysisOpen] = React.useState(false);
  const [overallAnalysisOpen, setOverallAnalysisOpen] = React.useState(false);
  const [trendCurvesAnalysisOpen, setTrendCurvesAnalysisOpen] =
    React.useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [barData, setBarData] = React.useState<BarData[]>([]);
  const [l1AnalysisData, setL1AnalysisData] = React.useState<DataNode | null>(
    null
  );
  const [l2AnalysisData, setL2AnalysisData] = React.useState<DataNode | null>(
    null
  );
  const [l3AnalysisData, setL3AnalysisData] = React.useState<DataNode | null>(
    null
  );

  const [trendsCurveData, setTrendsCurveData] = useState<LineGraphData>({
    labels: [],
    datasets: [],
  });

  const user = useSelector((state: RootState) => state.user.userInfo);
  const { id } = useParams();
  const numericSnapshotId = Number(id);
  const fetchDetails = async (page: number) => {
    const detailsParams = {
      page: page,
      numberOfTrends: 1,
      postsPerTrend: 1,
    };
    try {
      const snapDetails = await getSnapshotDetails(
        numericSnapshotId,
        user,
        detailsParams
      );
      // Set Bar Graph Data
      const trendCurveResponse = snapDetails?.data.lineGraphData;

      const factoredTrendCurveResponse =
        getFactoredTrendCurveResponse(trendCurveResponse);

      setTrendsCurveData(factoredTrendCurveResponse);
      setBarData(snapDetails.data.barGraphData);

      //Set L1 Color Analysis Data
      if (snapDetails.data.l1ColorAnalysis) {
        const dataL1: DataNode = snapDetails.data.l1ColorAnalysis;
        setL1AnalysisData(dataL1);
      }

      // Set L2 Color Analysis Data
      if (snapDetails.data.l2ColorAnalysis) {
        const dataL2: DataNode = snapDetails.data.l2ColorAnalysis.children;
        setL2AnalysisData(dataL2);
        //Set L3 Color Analysis Data
        const colorForLevel3 =
          snapDetails?.data?.snapshot?.genericFilters?.trendAnalysisFilters
            ?.colorForLevel3;
        if (colorForLevel3) {
          const dataL3 = dataL2?.children?.find(
            d => d.color === colorForLevel3
          );
          if (dataL3) {
            setL3AnalysisData(dataL3 || null);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching snapshot details:", error);
    }
  };

  useEffect(() => {
    id && fetchDetails(1);
  }, []);

  const toggleColorAnalysis = () => {
    setColorAnalysisOpen(prev => !prev);
  };

  const toggleOverallAnalysis = () => {
    setOverallAnalysisOpen(prev => !prev);
  };

  const toggleTrendCurveAnalysis = () => {
    setTrendCurvesAnalysisOpen(prev => !prev);
  };

  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      {l1AnalysisData && (
        <div className="flex items-center justify-between px-1 w-full">
          <div className="flex">
            <div className="cursor-pointer" onClick={toggleColorAnalysis}>
              {colorAnalysisOpen ? <Minus /> : <Plus />}
            </div>
            <div className="transition-all duration-500 ease-in-out opacity-100 font-semibold max-w-full ml-1 text-secondary">
              Color Analysis
            </div>
          </div>
          {colorAnalysisOpen && (
            <div
              className="right-2 text-right text-xs underline font-bold	mt-2 cursor-pointer"
              onClick={() =>
                navigate(`/trend-snapshot/details/color-analysis/${id}`)
              }
            >
              Expand
            </div>
          )}
        </div>
      )}
      {colorAnalysisOpen && (
        <div className="mx-3 w-[430px]">
          <ColorAnalysisSnapShot
            data1={l1AnalysisData}
            data2={l2AnalysisData}
            data3={l3AnalysisData}
          />
        </div>
      )}
      <div className="flex items-center justify-between px-1 w-full">
        <div className="flex">
          <div className="cursor-pointer" onClick={toggleOverallAnalysis}>
            {overallAnalysisOpen ? <Minus /> : <Plus />}
          </div>
          <div className="transition-all duration-500 ease-in-out opacity-100 font-semibold max-w-full ml-1 text-secondary">
            Overall Analysis
          </div>
        </div>
        {overallAnalysisOpen && (
          <div
            className="right-2 text-right text-xs underline font-bold	mt-2 cursor-pointer"
            onClick={() =>
              navigate(`/trend-snapshot/details/overall-analysis/${id}`)
            }
          >
            Expand
          </div>
        )}
      </div>
      {overallAnalysisOpen && (
        <>
          <div className="mx-3 w-[430px]">
            <div className="relative overflow-x-scroll scrollbar-custom">
              <div className="" ref={scrollContainerRef}>
                <OverallAnalysisGraph barData={barData} />
              </div>
            </div>
          </div>
        </>
      )}
      <div className="flex items-center justify-between px-1 w-full">
        <div className="flex">
          <div className="cursor-pointer" onClick={toggleTrendCurveAnalysis}>
            {trendCurvesAnalysisOpen ? <Minus /> : <Plus />}
          </div>
          <div className="transition-all duration-500 ease-in-out opacity-100 font-semibold max-w-full ml-1 text-secondary">
            Trend Curves
          </div>
        </div>
        {trendCurvesAnalysisOpen && (
          <div
            className="right-2 text-right text-xs underline font-bold	mt-2 cursor-pointer"
            onClick={() =>
              navigate(`/trend-snapshot/details/Trend-Curve-analysis/${id}`)
            }
          >
            Expand
          </div>
        )}
      </div>
      {trendCurvesAnalysisOpen && (
        <>
          <div className="mx-3 w-[410px]">
            <div className="text-sm">Filter By</div>
            <Dropdown
              label={"Filter By"}
              options={["Filter By"]}
              onSelect={option => {}}
              trendSnapshotView={true}
            />

            <TrendCurvesGraph
              lineGraphData={trendsCurveData}
              width={430}
              trendsCurveExpandedView={false}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default GraphsSidebarForSnapshot;
